const sortDocsByVersionDesc = (docs = []) =>
    [...docs].sort((a, b) => {
        if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
        const va = a.correctionVersion ?? 0;
        const vb = b.correctionVersion ?? 0;
        if (vb !== va) return vb - va;
        return new Date(b.created || 0) - new Date(a.created || 0);
    });

// Cálculo entrega 3 docs principais (Memorial + Relatório + PJC) na mesma rodada
// com correctionVersion=0 e timestamps em segundos. A janela cobre essa rodada
// sem misturar com correções subsequentes (intervalo típico entre rodadas: horas
// ou dias).
const SAME_ROUND_WINDOW_MS = 10 * 60 * 1000;

const splitMainsByRound = (mainsTrue) => {
    if (!mainsTrue.length) return { current: [], previous: [] };

    const sorted = sortDocsByVersionDesc(mainsTrue);
    const topVersion = sorted[0].correctionVersion ?? 0;

    const sameVersion = sorted.filter(d => (d.correctionVersion ?? 0) === topVersion);
    if (sameVersion.length !== sorted.length) {
        // Backend já diferencia rodadas via correctionVersion (Petição com correções).
        // Confia no campo: rodada atual = docs com a versão mais alta.
        const sameVersionSet = new Set(sameVersion);
        return {
            current: sameVersion,
            previous: sorted.filter(d => !sameVersionSet.has(d)),
        };
    }

    // Backend não diferencia rodadas (Cálculo: correctionVersion=0 em todos).
    // Fallback: agrupa por janela de tempo ancorada no mais recente.
    const anchorTs = new Date(sorted[0].created || 0).getTime();
    const current = [];
    const previous = [];
    for (const doc of sorted) {
        const ts = new Date(doc.created || 0).getTime();
        if (anchorTs - ts <= SAME_ROUND_WINDOW_MS) current.push(doc);
        else previous.push(doc);
    }
    return { current, previous };
};

const hasBackendCategory = (docs) => docs.every(d => typeof d.category === 'string');

const categorizeByBackendCategory = (docs) => ({
    main: sortDocsByVersionDesc(docs.filter(d => d.category === 'main')),
    previousVersions: sortDocsByVersionDesc(docs.filter(d => d.category === 'previous-version')),
    complementary: sortDocsByVersionDesc(docs.filter(d => d.category === 'complementary')),
});

const categorizeServiceDocuments = (docs = []) => {
    if (!docs.length) return { main: [], previousVersions: [], complementary: [] };

    if (hasBackendCategory(docs)) {
        const grouped = categorizeByBackendCategory(docs);
        if (grouped.main.length > 0) return grouped;
    }

    const mainsTrue = docs.filter(d => d.isServiceMainDocument === true);
    const mainsOther = docs.filter(d => d.isServiceMainDocument !== true);

    if (mainsTrue.length > 0) {
        const { current, previous } = splitMainsByRound(mainsTrue);
        return {
            main: current,
            previousVersions: previous,
            complementary: sortDocsByVersionDesc(mainsOther),
        };
    }

    const sorted = sortDocsByVersionDesc(docs);
    return {
        main: sorted.length ? [sorted[0]] : [],
        previousVersions: sorted.slice(1),
        complementary: [],
    };
};

export { sortDocsByVersionDesc, categorizeServiceDocuments };
